import { useState } from "react";
import { AdminTableSkeleton } from "../../components/admin/AdminTableSkeleton";
import { ModalShell } from "../../components/ModalShell";
import { Toast, useToast } from "../../components/Toast";
import { RevealSecureNoteModal } from "../../components/dashboard/secure-notes/RevealSecureNoteModal";
import { type AdminSecureNote, useAdminSecureNotes } from "../../hooks/useAdminSecureNotes";
import { formatDateTime, formatDateTimeCompact, formatRelativeTime } from "../../utils/formatDateTime";
import modalStyles from "../../components/dashboard/secure-notes/SecureNoteModals.module.css";
import styles from "../AdminInstances.module.css";
import flagStyles from "./AdminFeatureFlags.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

// Admin view of every customer's active secure notes. Reveal opens the
// audited value modal; delete reaps a note once its secret has been used.
export const AdminSecureNotes = (): JSX.Element => {
  const { status, notes, errorMessage, reveal, deleteNote } = useAdminSecureNotes();
  const { toast, showToast, dismissToast } = useToast();
  const [revealing, setRevealing] = useState<AdminSecureNote | null>(null);
  const [deleting, setDeleting] = useState<AdminSecureNote | null>(null);
  const [deleteBusy, setDeleteBusy] = useState<boolean>(false);

  const handleDelete = async (note: AdminSecureNote): Promise<void> => {
    setDeleteBusy(true);
    const result = await deleteNote(note.id);
    setDeleteBusy(false);
    setDeleting(null);
    showToast(
      result.ok ? `Deleted "${note.name}" (${note.ownerEmail}).` : `Delete failed: ${result.message}`,
      result.ok ? "success" : "error",
    );
  };

  return (
    <div className={styles.page}>
      <AdminPageIntro title="Secure notes">
        Secrets customers shared with the team. Reveals are audited; notes self-delete at expiry, or delete them here once used.
      </AdminPageIntro>

      <section className={styles.listSection}>
        {status === "loading" && (
          <AdminTableSkeleton
            headers={["Note", "Shared", "Expires", ""]}
            tableClassName={styles.table ?? ""}
          />
        )}
        {status === "error" && (
          <div className={styles.empty}>{errorMessage ?? "Failed to load."}</div>
        )}
        {status === "ready" && notes.length === 0 && (
          <div className={styles.empty} data-testid="admin-secure-notes-empty">
            No active secure notes.
          </div>
        )}
        {status === "ready" && notes.length > 0 && (
          <table className={styles.table} data-testid="admin-secure-notes-table">
            <thead>
              <tr>
                <th>Note</th>
                <th>Shared</th>
                <th>Expires</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {notes.map((note) => (
                <tr key={note.id} data-testid="admin-secure-note-row">
                  <td>
                    <span className={styles.noteName} title={note.name}>
                      {note.name}
                    </span>
                    <span className={styles.ownerLine}>
                      <span className={`${styles.ownerEmail} ${styles.ownerEmailWide}`} title={note.ownerEmail}>
                        {note.ownerEmail}
                      </span>
                    </span>
                  </td>
                  <td className={`${styles.muted} ${styles.nowrap}`}>
                    <span title={formatDateTime(note.createdAt)}>
                      {formatRelativeTime(note.createdAt)}
                    </span>
                  </td>
                  <td className={`${styles.muted} ${styles.nowrap}`} title={formatDateTime(note.expiresAt)}>
                    {formatDateTimeCompact(note.expiresAt)}
                  </td>
                  <td className={styles.actionCell}>
                    <div className={styles.actionCellInner}>
                      <button type="button" className={styles.secondaryBtn} onClick={() => setRevealing(note)}>
                        Reveal
                      </button>
                      <button type="button" className={styles.dangerBtn} onClick={() => setDeleting(note)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {revealing && (
        <RevealSecureNoteModal
          note={revealing}
          reveal={reveal}
          onClose={() => setRevealing(null)}
        />
      )}

      {deleting && (
        <ModalShell density="compact" onClose={() => setDeleting(null)} titleId="admin-delete-secure-note-title" size="sm">
          <header className={flagStyles.dialogHeader}>
            <h3 className={flagStyles.dialogTitle} id="admin-delete-secure-note-title">
              Delete secure note
            </h3>
            <button
              type="button"
              className={flagStyles.closeBtn}
              onClick={() => setDeleting(null)}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <p className={modalStyles.hint}>
            Permanently delete &quot;{deleting.name}&quot; from {deleting.ownerEmail}? Do this
            once the secret has been used — it can&apos;t be recovered.
          </p>
          <footer className={modalStyles.footer}>
            <button
              type="button"
              className={modalStyles.secondaryBtn}
              onClick={() => setDeleting(null)}
              disabled={deleteBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className={modalStyles.dangerBtn}
              onClick={() => void handleDelete(deleting)}
              disabled={deleteBusy}
            >
              {deleteBusy ? "Deleting…" : "Delete note"}
            </button>
          </footer>
        </ModalShell>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};
